/**
 * Fixed-timestep loop with an accumulator, feeding a variable-rate render.
 *
 * Float physics is fine (brief: "beatable", no determinism guarantee). The fixed
 * step is purely for STABLE FEEL — jump arcs and accel don't change with frame
 * rate. Render gets an interpolation alpha for smoothness between steps.
 */
export class GameLoop {
  /** Fixed physics step in seconds (60 Hz). */
  readonly step: number;
  private accumulator = 0;
  private maxFrame: number;

  constructor(
    private update: (dt: number) => void,
    // render gets the interpolation alpha AND the real (clamped) frame dt in seconds —
    // the dt drives render-ONLY, wall-clock effects (juice: shake/flash decay) so they are
    // frame-rate independent without touching the deterministic fixed step.
    private render: (alpha: number, dtSec: number) => void,
    hz = 60,
  ) {
    this.step = 1 / hz;
    // Clamp huge frame deltas (tab-out, breakpoints) to avoid a spiral of death.
    this.maxFrame = this.step * 5;
  }

  /** Advance by a real-time delta (seconds). Runs 0..N fixed steps, then renders. */
  tick(frameDelta: number): void {
    const fd = Math.min(frameDelta, this.maxFrame);
    this.accumulator += fd;
    while (this.accumulator >= this.step) {
      this.update(this.step);
      this.accumulator -= this.step;
    }
    this.render(this.accumulator / this.step, fd);
  }
}
